import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMatches } from 'react-router-dom';
import * as yup from 'yup';

import { getRoutePattern } from './getRoutePattern';

import { TOO_MANY_LOGIN_ATTEMPTS } from '@speeki/dictionary';
import {
    Button,
    Modal,
    ModalComponent,
    ModalProps,
    notify,
    TextareaField,
    TextField,
} from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import { SendPortalSupportRequestDocument } from '@graphql/generated/graphql';

import { useUserBasic } from '@utils/hooks/useUserBasic';

import { AuthService } from '@services/AuthService/AuthService';

import { useMutation, useReactiveVar } from '@apollo/client';
import { currentLanguageVar } from '@boot/reactiveVariables';
import { yupResolver } from '@hookform/resolvers/yup';
import { emailValidation } from '@shared/validations';

import { HoneypotField, SupportNote } from './ContactSupportModal.styled';

const SUPPORT_EMAIL = 'support@speeki.com';
const MESSAGE_MAX_LENGTH = 3000;
const NAME_MAX_LENGTH = 100;

interface ContactSupportModalProps extends Pick<
    ModalProps,
    'onClose' | 'open'
> {}

export const ContactSupportModal = ({
    onClose,
    open,
}: ContactSupportModalProps) => {
    const { t } = useTranslation();
    const language = useReactiveVar(currentLanguageVar);
    const matches = useMatches();
    const { data: userData } = useUserBasic();

    const {
        formState: { errors, isSubmitting },
        getValues,
        handleSubmit,
        register,
        reset,
        setValue,
        watch,
    } = useForm<ContactSupportFormValues>({
        defaultValues: { email: '', leaveBlank: '', message: '', name: '' },
        mode: 'onSubmit',
        resolver: yupResolver(ContactSupportSchema),
    });

    // Fill in once, from the first details that arrive: a later refresh of the
    // user must not put back a value the visitor has deliberately cleared.
    const prefilled = useRef(false);

    useEffect(() => {
        const user = userData?.user;
        if (!open || !user || prefilled.current) return;
        prefilled.current = true;

        const email = user.email || user.contactEmail;
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
        if (email && !getValues('email')) setValue('email', email);
        if (name && !getValues('name')) setValue('name', name);
    }, [open, userData]);

    const [sendRequest, { loading }] = useMutation(
        SendPortalSupportRequestDocument,
        {
            // Works before a company is picked; the mutation is public.
            context: { globalUrl: true },
            onCompleted: () => {
                reset();
                onClose();
                notify(t('CONTACT_SUPPORT_SENT')).SUCCESS();
            },
            onError: (error) => {
                // admin's throttle reuses the dictionary's login-throttle code.
                const rateLimited = error.graphQLErrors.some(
                    ({ extensions }) =>
                        extensions?.code === TOO_MANY_LOGIN_ATTEMPTS,
                );
                notify(
                    rateLimited
                        ? t('CONTACT_SUPPORT_TOO_MANY')
                        : t('CONTACT_SUPPORT_FAILED', { email: SUPPORT_EMAIL }),
                ).ERROR();
            },
        },
    );

    const onSubmit = handleSubmit((values) => {
        const tenantUuid = AuthService.getTenantUuid();
        const name = values.name?.trim();
        const page = getRoutePattern(matches);

        void sendRequest({
            variables: {
                input: {
                    email: values.email.trim(),
                    language,
                    message: values.message.trim(),
                    // The route pattern, never the address: it can carry
                    // identifiers, and the visitor's email travels with it.
                    ...(page ? { page } : {}),
                    ...(name ? { name } : {}),
                    ...(tenantUuid ? { tenantUuid } : {}),
                    ...(values.leaveBlank
                        ? { leaveBlank: values.leaveBlank }
                        : {}),
                },
            },
        });
    });

    const handleCancel = () => {
        reset();
        onClose();
    };

    const message = watch('message') ?? '';

    return (
        <ModalComponent
            closeButton
            onClose={handleCancel}
            open={open}
            preventCloseEsc
            preventCloseOutside
            width={648}
        >
            <Modal.Content variant="secondary">
                <Modal.Header>
                    <Modal.TitleSecondary center>
                        {t('CONTACT_SUPPORT_TITLE')}
                    </Modal.TitleSecondary>
                </Modal.Header>

                <StandardForm noValidate onSubmit={onSubmit}>
                    <SupportNote>{t('CONTACT_SUPPORT_NOTE')}</SupportNote>

                    <TextField
                        {...register('email')}
                        className="htmlToImageIgnore"
                        error={!!errors.email}
                        id="contact-support-email"
                        label={t('EMAIL')}
                        message={t(errors.email?.message || '')}
                        placeholder={t('EMAIL_PLACEHOLDER')}
                        testId="WIgNjE0"
                        type="email"
                    />

                    <TextField
                        {...register('name')}
                        className="htmlToImageIgnore"
                        error={!!errors.name}
                        id="contact-support-name"
                        label={t('CONTACT_SUPPORT_NAME_LABEL')}
                        message={t(errors.name?.message || '')}
                        testId="kHbmsZO"
                    />

                    <TextareaField
                        {...register('message')}
                        className="htmlToImageIgnore"
                        error={!!errors.message}
                        id="contact-support-message"
                        label={t('HOW_CAN_WE_HELP_YOU_TODAY')}
                        message={
                            errors.message
                                ? t(errors.message.message ?? '')
                                : t('CHARACTER_COUNT', {
                                      current: message.length,
                                      max: MESSAGE_MAX_LENGTH,
                                  })
                        }
                        rows={5}
                        testId="D9PfoAf"
                    />

                    <HoneypotField
                        {...register('leaveBlank')}
                        aria-hidden="true"
                        autoComplete="off"
                        tabIndex={-1}
                        type="text"
                    />
                </StandardForm>
            </Modal.Content>

            <Modal.Footer>
                <Modal.ButtonsWrapper>
                    <Button
                        onClick={handleCancel}
                        testId="button_cancel"
                        variant={'secondary'}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        disabled={isSubmitting || loading}
                        isLoading={loading || isSubmitting}
                        onClick={onSubmit}
                        testId="button_send"
                        variant={'primary'}
                    >
                        {t('CONTACT_SUPPORT_SEND')}
                    </Button>
                </Modal.ButtonsWrapper>
            </Modal.Footer>
        </ModalComponent>
    );
};

const ContactSupportSchema = yup.object({
    // The server's email check needs a domain suffix (jane@company is
    // rejected); yup's `.email()` accepts it, so check it here too.
    email: emailValidation.test(
        'has-domain-suffix',
        'INVALID_EMAIL',
        (value) => !value || /@[^@\s]+\.[^@\s]+$/.test(value),
    ),
    leaveBlank: yup.string().optional(),
    message: yup
        .string()
        .trim()
        .required('FIELD_CANNOT_BE_EMPTY')
        .max(MESSAGE_MAX_LENGTH, 'CHARACTER_LIMIT_REACHED'),
    name: yup
        .string()
        .trim()
        .max(NAME_MAX_LENGTH, 'CHARACTER_LIMIT_REACHED')
        .optional(),
});

type ContactSupportFormValues = yup.InferType<typeof ContactSupportSchema>;
